import { useEffect, useState } from "react";
import { getOutreachAuthUrl } from "../../services/firebase";
import { Errors } from "../../types/errors";

/**
 * Hook for Outreach OAuth - provides auth URL for reconnecting.
 * NOTE: OAuth code processing is handled by OutreachRedirect.tsx.
 * This hook ONLY provides the openOutreachAuthUrl function for Settings page reconnecting.
 */
export const useConnectOutreach = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const getConnectOutreachUrl = async () => {
      try {
        setIsProcessing(true);
        setError("");

        const { result, status } = await getOutreachAuthUrl();

        if (status === 200 && result?.outreachAuthUrl) {
          setUrl(result.outreachAuthUrl);
        } else {
          setError(
            `Error connecting to Outreach (Code: ${Errors.CONNECT_OUTREACH_GENERATE_URL_FUNCTION_ERROR}). Please contact us for help.`
          );
        }
      } catch {
        setError(
          `Error connecting to Outreach (Code: ${Errors.CONNECT_OUTREACH_GENERATE_URL_FUNCTION_FAILURE}). Please contact us for help.`
        );
      } finally {
        setIsProcessing(false);
      }
    };

    getConnectOutreachUrl();
  }, []);

  // The link is fetched again on every click: one fetched when the page
  // loaded goes stale when the scopes change server-side, and a page left
  // open would keep sending people to a link Outreach now rejects. The popup
  // opens before the await so the browser still counts it as a click.
  const openOutreachAuthUrl = async () => {
    const width = 800;
    const height = 800;
    const left = (window.innerWidth - width) / 2 + window.screenX;
    const top = (window.innerHeight - height) / 2 + window.screenY;
    const params = `width=${width},height=${height},scrollbars=yes,resizable=yes,top=${top},left=${left}`;
    const newWindow = window.open("", "Authorize Outreach", params);

    let freshUrl = "";
    try {
      const { result, status } = await getOutreachAuthUrl();
      if (status === 200 && result?.outreachAuthUrl) {
        freshUrl = result.outreachAuthUrl;
        setUrl(freshUrl);
      }
    } catch {
      // Fall back to the link fetched when the page loaded.
    }

    const target = freshUrl || url;
    if (!target) {
      newWindow?.close();
      setError(
        `Error connecting to Outreach (Code: ${Errors.CONNECT_OUTREACH_GENERATE_URL_FUNCTION_FAILURE}). Please contact us for help.`
      );
      return;
    }
    if (newWindow) {
      newWindow.location.href = target;
      newWindow.focus();
    } else {
      window.open(target, "Authorize Outreach", params)?.focus();
    }
  };

  return {
    isProcessing,
    url,
    error,
    openOutreachAuthUrl,
  };
};
